import React, {useContext, useEffect, useState} from "react";
import {Tab, Tabs, ThemeProvider} from "@mui/material";
import {translateService, Translate} from "../../../services/TranslateService";
import {basePrice} from "../../MakeReservation/pricingCalculations";
import useFilter, {SortOptions} from "../Filter";
import {IVehicleSearch} from "../../../interfaces/VehicleSearch";
import {DataContext} from "../../../contexts/dataContext";
import {yellowTheme} from "../../../CustomModalStyles";
import styles from '../SearchFilter.module.scss';
import CustomTooltip from "../../CustomTooltip";
import {cx} from "../../MakeReservation/util";
export const SearchTab = () => { // for normal reservation tab sorting only, heavier functionality with all filters included
    const { sortVehicles, lowestPrice, lowestDistance } = useFilter();
    const { modelId, changeModelId, showingModelsResults, changeShowingModelsResults, sortingTab, changeSortingTab } = useContext(DataContext);
    const [value, setValue] = useState(sortingTab || "distance");

    const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
        setValue(newValue);
        if (sortingTab !== newValue) {
            changeSortingTab(newValue);
        }

        if (newValue === 'models') {
            changeShowingModelsResults(true);
            changeModelId(undefined);
        } else {
            if (showingModelsResults) {
                sessionStorage.setItem('sortWait', '1');
                changeShowingModelsResults(false);
                changeModelId(undefined);
            } else {
                sortVehicles(newValue as SortOptions);
            }
        }
    };

    useEffect(() => {
        if (showingModelsResults && value !== 'models') {
            setValue('models');
            changeSortingTab('models');
        }
    }, [showingModelsResults]);

    return (
        <div className={styles['searchTabContainer']}>
            <div style={{width: modelId?.length === 1 ? '90%' : '100%'}}>
                <ThemeProvider theme={yellowTheme}>
                    <Tabs value={value} onChange={handleChange} variant="fullWidth"  TabIndicatorProps={{ style: { backgroundColor: "white" } }}>
                        <Tab label={`${translateService.translate('vehicleSearch.closest')} ${lowestDistance} km`} value="distance" id="r24-sort-tab-distance"
                             classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }}/>
                        <Tab label={`${translateService.translate('vehicleSearch.cheapestTab')} ${lowestPrice} €`} value="models" id="r24-sort-tab-cheapest"
                             classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }} />
                        <Tab label={translateService.translate('vehicleSearch.largestTab')} value="length" id="r24-sort-tab-largest"
                             classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }} />
                    </Tabs>
                </ThemeProvider>
            </div>
            { modelId?.length === 1 &&
                <CustomTooltip className="tooltip-link" zIndex={2}
                       content={<Translate content={'vehicleSearch.removedModelFilterBtn'}/>}>
                    <button onClick={() => { sessionStorage.setItem('sortWait', '1'); changeModelId(undefined); }}
                            className={cx("btn btn-info btn-info-primary btn-all",
                                styles['backToModelsBtn'])}>
                        &lt;&lt;
                    </button>
                </CustomTooltip> }
        </div>
    );
};

interface IOneWaySearchTabProps {
    oneWayVehicleList?: IVehicleSearch[];
    setSearchResults: Function;
    noVatPrices: boolean;
}

export const OneWaySearchTab = ({ oneWayVehicleList, setSearchResults, noVatPrices }: IOneWaySearchTabProps) => {
    // for one-way reservation tab sorting only, lighter functionality with tab sort for distance, price and volume only
    const [originalList, setOriginalList] = useState<IVehicleSearch[]>(oneWayVehicleList ?? []);
    const [lowestPrice, setLowestPrice] = useState<number>(0);
    const [lowestDistance, setLowestDistance] = useState<number>(0);
    const [highestTrunkVolume, setHighestTrunkVolume] = useState<number>(0);
    const [value, setValue] = useState( "distance");

    useEffect(() => {
        if (oneWayVehicleList) { setOriginalList(oneWayVehicleList)}
    }, [oneWayVehicleList])

    useEffect(() => {
        if(originalList && originalList.length !== 0){
            const lowestDistance = originalList?.reduce((prev, current) => (prev.distance < current.distance) ? prev : current);
            setLowestDistance(Math.round((+lowestDistance.distance / 1000) * 10) / 10);
            const lowestPriceVehicle = originalList?.reduce((prev: any, current: any) => {
                const prevPricing = noVatPrices ? prev.pricingDataNoVat : prev.pricingData;
                const currentPricing = noVatPrices ? current.pricingDataNoVat : current.pricingData;
                return (prevPricing.base.totalPrice < currentPricing.base.totalPrice) ? prev : current;
            });
            if (lowestPriceVehicle) {
                setLowestPrice(basePrice(lowestPriceVehicle, noVatPrices));
            }
            const highestVolumeVehicle = originalList?.reduce((prev: any, current: any) => (prev.vehicleModelData?.dimensions?.trunkVolume < current.vehicleModelData?.dimensions?.trunkVolume) ? current : prev);
            if (highestVolumeVehicle?.vehicleModelData?.dimensions?.trunkVolume !== undefined) {
                setHighestTrunkVolume(highestVolumeVehicle.vehicleModelData.dimensions.trunkVolume);
            }
        }
    }, [oneWayVehicleList]);

    const sortVehicles = (option: SortOptions) => {
        let sortedVehicles: IVehicleSearch[] = [...(originalList || [])]; // provide a default empty array when filteredVehicles is undefined
        sortedVehicles.sort((a, b) => {
            // Sort by reserved status first
            if (a.availabilityData?.available && !b.availabilityData?.available) return -1; // if a is reserved and b is not, sort a to the end
            if (!a.availabilityData?.available && b.availabilityData?.available) return 1; // if b is reserved and a is not, sort b to the end
            // Then sort by the selected option
            switch (option) {
                case SortOptions.Distance:
                    return a.distance - b.distance;
                case SortOptions.Price:
                    const aPricing = noVatPrices ? a.pricingDataNoVat : a.pricingData;
                    const bPricing = noVatPrices ? b.pricingDataNoVat : b.pricingData;
                    return aPricing.base.totalPrice - bPricing.base.totalPrice;
                case SortOptions.TrunkVolume:
                    return b.vehicleModelData!.dimensions!.trunkVolume! - a.vehicleModelData!.dimensions!.trunkVolume!;
                default:
                    return 0;
            }
        });

        setSearchResults(sortedVehicles);
        return sortedVehicles;
    };

    const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
        setValue(newValue);
        if(newValue === 'distance') {
            sortVehicles('distance' as SortOptions)
        }
        if(newValue === 'price') {
            sortVehicles('price' as SortOptions)
        }
        if(newValue === 'trunkvolume') {
            sortVehicles('trunkvolume' as SortOptions)
        }
    };

    return (
        <div className={'mt-3'}>
            <ThemeProvider theme={yellowTheme}>
                <Tabs value={value} onChange={handleChange} variant="fullWidth"  TabIndicatorProps={{ style: { backgroundColor: "white" } }}>
                    <Tab label={`${translateService.translate('vehicleSearch.closest')} ${lowestDistance} km`} value="distance" id="r24-ow-sort-tab-distance" classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }}/>
                    <Tab label={`${translateService.translate('vehicleSearch.cheapest')} ${lowestPrice} €`} value="price" id="r24-ow-sort-tab-cheapest" classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }} />
                    <Tab label={`${translateService.translate('vehicleSearch.trunkSize')} ${highestTrunkVolume} m³`} value="trunkvolume" id="r24-ow-sort-tab-trunk" classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }} />
                </Tabs>
            </ThemeProvider>
        </div>
    );
};